/**
 * Controllers barrel + wiring.
 *
 * `createControllers(ctx)` builds the controllers, closes the cycles between them (the
 * project controller drives mapping/rig/library on import; mapping/rig/library persist through the
 * project controller) and installs the debounced autosave.
 *
 * It also sets `ctx.rigTools = controllers.rig`, so the viewport gets its skinning tools without
 * `main.ts` having to remember (the contract allows either side to do it).
 */

import type { EditorContext } from '../context';
import { createSaveScheduler } from './internal';
import type { ControllerDeps, InternalRigController } from './internal';
import { createAnimationImportController } from './animationImportController';
import { createExportController } from './exportController';
import { createLibraryController } from './libraryController';
import { createMappingController } from './mappingController';
import { createPreviewController } from './previewController';
import { createProjectController } from './projectController';
import { createRigController } from './rigController';
import type { Controllers } from './types';

export type {
  Controllers,
  ExportController,
  ExportGlbOptions,
  LibraryController,
  MappingController,
  PreviewController,
  ProjectController,
  RigController,
} from './types';
export { LIBRARY_JSON_FILENAME } from './libraryController';
export { SAVE_DEBOUNCE_MS } from './internal';

export interface ControllersOptions {
  /**
   * Called when the project save scheduler starts or finishes owing IndexedDB a write. Defaults
   * to `store.actions.setSaving`; `main.ts` overrides it so the draft autosave and the project
   * save share one flag instead of clearing each other's (see `createPendingSaveTracker`).
   */
  onPendingChange?(pending: boolean): void;
}

export function createControllers(ctx: EditorContext, options?: ControllersOptions): Controllers {
  const reportPending =
    options?.onPendingChange ?? ((pending: boolean) => ctx.store.actions.setSaving(pending));
  /*
   * The scope travels with the save. `saveProjectState(scope)` drops the write when the project
   * it was scheduled for is no longer the open one, instead of writing the store's (now
   * different) contents onto whatever record happens to be current — audit §2.1 #1 Path C.
   */
  const scheduler = createSaveScheduler(
    (scope) => deps.controllers.project.saveProjectState(scope),
    undefined,
    reportPending,
  );

  /**
   * Depth of the "this placement change is a project switch, not an edit" suppression (see
   * `ControllerDeps.suppressPlacementAutosave`). A counter rather than a boolean so nesting can
   * never leave it stuck on.
   */
  let placementAutosaveSuppressed = 0;

  // `controllers` and `rig` are assigned immediately below; nothing reads them during
  // construction, only from inside a method call.
  const deps = {
    ctx,
    // `project` is declared below but only read when these run, which is never during construction.
    scheduleSave: () => scheduler.schedule(project.currentProjectId()),
    saveNow: () => scheduler.saveNow(project.currentProjectId()),
    flushSaves: () => scheduler.flush(),
    suppressPlacementAutosave<T>(run: () => T): T {
      placementAutosaveSuppressed++;
      try {
        return run();
      } finally {
        placementAutosaveSuppressed--;
      }
    },
  } as ControllerDeps;

  const rig: InternalRigController = createRigController(deps);
  deps.rig = rig;

  const project = createProjectController(deps);
  const mapping = createMappingController(deps);
  const library = createLibraryController(deps);
  const animationImport = createAnimationImportController(deps);
  const exporter = createExportController(deps);
  const preview = createPreviewController(deps);

  /**
   * Joint placement is edited continuously, so a drag (`beginJointDrag` … `endJointDrag`, i.e.
   * `history.coalescing`) is still debounced. A single move — a number typed into the panel, or
   * the last one before the drag ends — is written immediately instead: `beforeunload` cannot
   * await IndexedDB, so a 300 ms timer is not a safe place to keep the user's work (wave-5
   * review finding 6). Never dispatches — the scheduler only calls repositories.
   *
   * The first line is the load-bearing one: a placement change made by a PROJECT SWITCH is not an
   * edit and must not reach IndexedDB at all. Writing it would stamp `placement: null` (and, via
   * `saveProjectState`'s `delete next.skinData`, no weights either) onto whichever record is
   * current — see `ControllerDeps.suppressPlacementAutosave`. Checked here, explicitly, so the
   * guarantee does not depend on the order of statements in `projectController.loadIntoSession`.
   */
  const unsubscribePlacement = ctx.store.select(
    (state) => state.rig.placement,
    () => {
      if (placementAutosaveSuppressed > 0) return;
      const id = project.currentProjectId();
      if (!id) return;
      if (ctx.store.history.coalescing) scheduler.schedule(id);
      else void scheduler.saveNow(id);
    },
  );

  const controllers: Controllers = {
    project,
    mapping,
    rig,
    library,
    animationImport,
    export: exporter,
    preview,
    flushPendingSaves: () => scheduler.flush(),
    hasPendingSaves: () => scheduler.isPending(),
    dispose(): void {
      scheduler.cancel();
      unsubscribePlacement();
      preview.dispose();
      rig.dispose();
      // The session owns the current import's GPU resources (geometries, materials, textures) and
      // releases them in `setModel` / `clear()`. Tearing the controllers down without it leaves
      // the last import owned but never released — which the DEV headless harness turns into a
      // leak per scenario, because it builds a fresh controller set for each one. Last, because
      // `preview.dispose()` above still reads what the session holds.
      ctx.session.clear();
    },
  };

  deps.controllers = controllers;
  ctx.rigTools = rig;

  return controllers;
}
